import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './views/Login';
import Configuration from './views/Configuration';
import IntegrationWiki from './views/IntegrationWiki';
import LayoutComponent from './components/App/LayoutComponent';
import ProtectedRoute from './components/App/ProtectedRoute';
import ResetPassword from './views/ResetPassword';
import SalesDashboard from './views/SalesDashboard';
import CompanyCreation from './views/CompanyCreation';
import VerifyRegistration from './views/VerifyRegistration';
import AcceptInvitation from './views/AcceptInvitation';
import WhatsAppVerify from './views/WhatsAppVerify';
import ConversationalAssistant from './views/ConversationalAssistant';
import CompanySelection from './views/CompanySelection';
import BackOffice from './views/BackOffice';
import SubscriptionCheckoutPlaceholder from './views/SubscriptionCheckoutPlaceholder';
import Dashboard from './views/Dashboard';
import Areas from './views/Areas';
import AreaCreate from './views/AreaCreate';
import Patterns from './views/Patterns';
import PatternCreateTemplate from './views/PatternCreateTemplate';
import PatternCreateArea from './views/PatternCreateArea';
import Measurements from './views/Measurements';
import MeasurementList from './views/MeasurementList';
import MeasurementReportPage from './views/MeasurementReportPage';
import MeasurementAssignAreas from './views/MeasurementAssignAreas';
import MeasurementAssignPatterns from './views/MeasurementAssignPatterns';
import MeasurementEditLocation from './views/MeasurementEditLocation';
import MeasurementImport from './views/MeasurementImport';
import ReportTemplateWizard from './views/ReportTemplateWizard';
import MetricEditor from './views/MetricEditor';
import Reports from './views/Reports';
import SavedReports from './views/SavedReports';
import SavedReportView from './views/SavedReportView';
import ReportTemplateSettings from './views/ReportTemplateSettings';
import AreaTypeEditor from './views/AreaTypeEditor';
import Devices from './views/Devices';
import Operations from './views/Operations';
import OperationCreate from './views/OperationCreate';
import OperationDetailView from './components/Operations/OperationDetailView';
import MissionDetailView from './views/MissionDetailView';
import DeviceDetailView from './views/DeviceDetailView';
import SlamMaps from './views/SlamMaps';
import { getDefaultRoute, getUserRoleCode, isMasterUser } from './helpers/accessControl';

import '../src/style/Global.css';
import { ConfigProvider, Grid, theme as antdTheme } from 'antd';
import { ThemeProvider, useTheme } from './context/ThemeContext';

const DefaultRedirect: React.FC = () => {
    const { currentUser } = useAuth();

    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    const fallbackRoute = getDefaultRoute(
        isMasterUser(currentUser),
        getUserRoleCode(currentUser)
    );

    return <Navigate to={fallbackRoute} replace />;
};

const AreaEditRedirect: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/areas/new?areaId=${id}`} replace />;
};


const ThemedApp: React.FC = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { themeMode } = useTheme();
    const isDarkMode = themeMode === 'dark';

    return (
    <ConfigProvider
        theme={{
          algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          token: {
            // Seed Token
            colorPrimary: '#1677ff',
            
            borderRadius: 5,
            borderRadiusLG: 14,
            fontSize: isMobile ? 14 : 13,
            controlHeight: isMobile ? 36 : 28,
            sizeUnit: 3,
            // Alias Token
            colorBgBase: isDarkMode ? '#0f172a' : '#ffffff',
            colorBgLayout: isDarkMode ? '#0f172a' : '#f5f7fb',
            colorBgContainer: isDarkMode ? '#1e293b' : '#FFFFFF',
            colorBgElevated: isDarkMode ? '#263449' : '#ffffff',
            colorText: isDarkMode ? '#e2e8f0' : '#0f172a',
            colorTextSecondary: isDarkMode ? '#94a3b8' : '#64748b',
            colorBorder: isDarkMode ? 'rgba(148, 163, 184, 0.24)' : 'rgba(226, 232, 240, 0.95)',
            colorBorderSecondary: isDarkMode ? 'rgba(148, 163, 184, 0.18)' : 'rgba(226, 232, 240, 0.95)',
            boxShadowSecondary: isDarkMode ? '0 12px 28px rgba(2, 6, 23, 0.34)' : '0 10px 24px rgba(15, 23, 42, 0.04)',
          },
        }}
      >
        <AuthProvider>
            <Router>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<Navigate replace to="/login" />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/create-company" element={
                        <ProtectedRoute>
                            <CompanyCreation />
                        </ProtectedRoute>
                    } />
                    <Route path="/subscription-checkout" element={
                        <ProtectedRoute>
                            <SubscriptionCheckoutPlaceholder />
                        </ProtectedRoute>
                    } />
                    <Route path="/verify-registration/:token" element={<VerifyRegistration />} />
                    <Route path="/verify-registration/:token/:userId" element={<VerifyRegistration />} />
                    <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
                    <Route path="/resetpassword/:token/:userId" element={<ResetPassword />} />
                    <Route path="/whatsapp/verify-number" element={<WhatsAppVerify />} />
                    <Route path="/select-company" element={
                        <ProtectedRoute>
                            <CompanySelection />
                        </ProtectedRoute>
                    } />
                    <Route path="/back-office" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <BackOffice />
                        </ProtectedRoute>
                    } />
                    
                    {/* Protected Core User Management Routes */}
                    <Route path="/configuration" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <Configuration />
                        </ProtectedRoute>
                    } />
                    <Route path="/docs/integrations" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <IntegrationWiki />
                        </ProtectedRoute>
                    } />
                    <Route path="/sales-dashboard" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <SalesDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/assistant" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <ConversationalAssistant />
                        </ProtectedRoute>
                    } />
                    <Route path="/areas/new" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <AreaCreate />
                        </ProtectedRoute>
                    } />
                    <Route path="/areas/:id/edit" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <AreaEditRedirect />
                        </ProtectedRoute>
                    } />
                    <Route path="/areas" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <Areas />
                        </ProtectedRoute>
                    } />
                    <Route path="/patterns" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <Patterns />
                        </ProtectedRoute>
                    } />
                    <Route path="/patterns/create/template" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <PatternCreateTemplate />
                        </ProtectedRoute>
                    } />
                    <Route path="/patterns/create/area" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <PatternCreateArea />
                        </ProtectedRoute>
                    } />
                    <Route path="/measurements" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <Measurements />
                        </ProtectedRoute>
                    } />
                    <Route path="/measurement-list" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MeasurementList />
                        </ProtectedRoute>
                    } />
                    <Route path="/devices" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <Devices />
                        </ProtectedRoute>
                    } />
                    <Route path="/operations/new" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <OperationCreate />
                        </ProtectedRoute>
                    } />
                    <Route path="/operations/missions/:missionId" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MissionDetailView />
                        </ProtectedRoute>
                    } />
                    <Route path="/operations" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <Operations />
                        </ProtectedRoute>
                    } />
                    <Route path="/operations/:id" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <OperationDetailView />
                        </ProtectedRoute>
                    } />
                    <Route path="/operations/device/:deviceId" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <DeviceDetailView />
                        </ProtectedRoute>
                    } />
                    <Route path="/slam-maps" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <SlamMaps />
                        </ProtectedRoute>
                    } />
                    <Route path="/measurements/:id" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MeasurementReportPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/measurements/import" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MeasurementImport />
                        </ProtectedRoute>
                    } />
                    <Route path="/measurements/assign-areas" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MeasurementAssignAreas />
                        </ProtectedRoute>
                    } />
                    <Route path="/measurements/assign-patterns" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MeasurementAssignPatterns />
                        </ProtectedRoute>
                    } />
                    <Route path="/measurements/:id/edit-location" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MeasurementEditLocation />
                        </ProtectedRoute>
                    } />
                    <Route path="/dashboard" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <Dashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/reports" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <Reports />
                        </ProtectedRoute>
                    } />
                    <Route path="/saved-reports" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <SavedReports />
                        </ProtectedRoute>
                    } />
                    <Route path="/saved-reports/:id" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <SavedReportView />
                        </ProtectedRoute>
                    } />
                    <Route path="/report-templates/settings" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <ReportTemplateSettings />
                        </ProtectedRoute>
                    } />
                    <Route path="/report-templates/wizard" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <ReportTemplateWizard />
                        </ProtectedRoute>
                    } />
                    <Route path="/metrics/new" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MetricEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/metrics/:id/edit" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MetricEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/back-office/metrics/new" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MetricEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/back-office/metrics/:id/edit" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <MetricEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/back-office/area-types/new" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <AreaTypeEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/back-office/area-types/:id/edit" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <AreaTypeEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/area-types/new" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <AreaTypeEditor />
                        </ProtectedRoute>
                    } />
                    <Route path="/area-types/:id/edit" element={
                        <ProtectedRoute layoutComponent={LayoutComponent}>
                            <AreaTypeEditor />
                        </ProtectedRoute>
                    } />
                    
                    {/* Fallback to configuration for any unmatched routes */}
                    <Route path="*" element={<DefaultRedirect />} />
                </Routes>
            </Router>
        </AuthProvider>
      </ConfigProvider>
    );
};

const App: React.FC = () => (
    <ThemeProvider>
        <ThemedApp />
    </ThemeProvider>
);

export default App;
